import { competitorServicesApi } from '@shared/api/competitor-services';
import {
  customerCompetitorServicesApi,
} from '@shared/api/customer-competitor-services';
import { listProductCompetitorServices } from '@shared/api/products';
import type { CompetitorService } from '@shared/types/competitor-service';
import type {
  CompetitorServiceTarget,
  CustomerCompetitorService,
} from '@shared/types/customer-competitor-service';
import type { ProductCompetitorService } from '@shared/types/product-competitor-service';

/// 競合サービスマスター (`competitor_services`) を選択肢用に全件ロードするストア。
///
/// `CompetitorServicesPanel` (顧客詳細) と `ProductCompetitorsEditor` (商材詳細) が
/// 他社ソリューションを select する候補に使う。
/// `/api/competitor-services` は typed API を返すため domain parser は通さず保持する
/// (#487 の parse-don't-validate は業務モデルに適用する方針で、本カタログは対象外)。
interface OptionsState {
  data: CompetitorService[];
  loading: boolean;
  error: string | null;
}

const optionsState = $state<OptionsState>({ data: [], loading: false, error: null });
let optionsToken = 0;

export const competitorServiceOptionsStore = {
  get data(): CompetitorService[] {
    return optionsState.data;
  },
  get loading(): boolean {
    return optionsState.loading;
  },
  get error(): string | null {
    return optionsState.error;
  },
  /// 競合サービスマスターを全件ロードする。stale は token で破棄。
  async load(): Promise<void> {
    const token = ++optionsToken;
    optionsState.loading = true;
    optionsState.error = null;
    try {
      const res = await competitorServicesApi.list();
      if (token !== optionsToken) return;
      optionsState.data = res.items;
    } catch (err) {
      if (token !== optionsToken) return;
      optionsState.error = err instanceof Error ? err.message : String(err);
    } finally {
      if (token === optionsToken) optionsState.loading = false;
    }
  },
  /// 新規作成 / PATCH 応答を options に反映する。id 一致行は上書き、未登録は追加し名前順ソートする。
  addOrReplace(solution: CompetitorService): void {
    const filtered = optionsState.data.filter((item) => item.id !== solution.id);
    optionsState.data = [...filtered, solution].sort((a, b) => {
      const byName = a.name.localeCompare(b.name, 'ja');
      return byName !== 0 ? byName : a.id.localeCompare(b.id);
    });
  },
  clear(): void {
    optionsToken++;
    optionsState.data = [];
    optionsState.loading = false;
    optionsState.error = null;
  },
};

/// 顧客 (firm / lawyer) ごとの利用ソリューション (`customer_competitor_services`) を
/// 保持するストア。
///
/// `CompetitorServicesPanel` で target (firm / lawyer) を引数に load し、
/// target が切り替わるたびに再ロードする。
/// 生データは typed レスポンスなので domain parser は通さず保持する。
interface CustomerServicesState {
  data: CustomerCompetitorService[];
  loading: boolean;
  error: string | null;
}

const customerServicesState = $state<CustomerServicesState>({
  data: [],
  loading: false,
  error: null,
});
let customerServicesToken = 0;

export const customerCompetitorServicesStore = {
  get data(): CustomerCompetitorService[] {
    return customerServicesState.data;
  },
  get loading(): boolean {
    return customerServicesState.loading;
  },
  get error(): string | null {
    return customerServicesState.error;
  },
  /// 指定 target の利用ソリューション一覧を取得する。stale は token で破棄。
  async load(target: CompetitorServiceTarget): Promise<void> {
    const token = ++customerServicesToken;
    customerServicesState.loading = true;
    customerServicesState.error = null;
    try {
      const res = await customerCompetitorServicesApi.list(target);
      if (token !== customerServicesToken) return;
      customerServicesState.data = res.items;
    } catch (err) {
      if (token !== customerServicesToken) return;
      customerServicesState.error = err instanceof Error ? err.message : String(err);
      customerServicesState.data = [];
    } finally {
      if (token === customerServicesToken) customerServicesState.loading = false;
    }
  },
  clear(): void {
    customerServicesToken++;
    customerServicesState.data = [];
    customerServicesState.loading = false;
    customerServicesState.error = null;
  },
};

/// 商材 (`products`) ごとの競合ソリューション設定 (`product_competitor_services`) を
/// 保持するストア。
///
/// `ProductCompetitorsEditor` で productId を引数に load する。
/// mutation (create / patch / delete) はコンポーネント側が API を直接呼び、
/// 成功後に `load()` で再取得する。
/// typed API を返すため domain parser は通さず保持する。
interface ProductCompetitorsState {
  data: ProductCompetitorService[];
  loading: boolean;
  error: string | null;
}

const productCompetitorsState = $state<ProductCompetitorsState>({
  data: [],
  loading: false,
  error: null,
});
let productCompetitorsToken = 0;

export const productCompetitorServicesStore = {
  get data(): ProductCompetitorService[] {
    return productCompetitorsState.data;
  },
  get loading(): boolean {
    return productCompetitorsState.loading;
  },
  get error(): string | null {
    return productCompetitorsState.error;
  },
  /// 指定商材の競合ソリューション一覧を取得する。stale は token で破棄。
  async load(productId: string): Promise<void> {
    const token = ++productCompetitorsToken;
    productCompetitorsState.loading = true;
    productCompetitorsState.error = null;
    try {
      const res = await listProductCompetitorServices(productId);
      if (token !== productCompetitorsToken) return;
      productCompetitorsState.data = res.items;
    } catch (err) {
      if (token !== productCompetitorsToken) return;
      productCompetitorsState.error = err instanceof Error ? err.message : String(err);
      productCompetitorsState.data = [];
    } finally {
      if (token === productCompetitorsToken) productCompetitorsState.loading = false;
    }
  },
  /// 追加成功後に store state へ楽観追加する。
  addRow(row: ProductCompetitorService): void {
    productCompetitorsState.data = [...productCompetitorsState.data, row];
  },
  /// 更新成功後に store state の対象行を置き換える。
  updateRow(updated: ProductCompetitorService): void {
    productCompetitorsState.data = productCompetitorsState.data.map((item) =>
      item.id === updated.id ? updated : item,
    );
  },
  /// 削除成功後に store state から除外する。
  removeRow(id: string): void {
    productCompetitorsState.data = productCompetitorsState.data.filter((item) => item.id !== id);
  },
  clear(): void {
    productCompetitorsToken++;
    productCompetitorsState.data = [];
    productCompetitorsState.loading = false;
    productCompetitorsState.error = null;
  },
};
